/**
 * factoryReset.ts — Restablecimiento de fábrica y compactación del almacenamiento local.
 *
 * QUÉ HACE:
 *  - `factoryResetLocalData()`: borra TODOS los datos locales (localStorage,
 *    bases IndexedDB del espejo y del servicio de persistencia, y caches del
 *    Cache Storage) para dejar la app como recién instalada. No desregistra el
 *    service worker de MSW: se re-registra solo al recargar.
 *  - `compactLocalStorage()`: libera espacio sin borrar datos de negocio:
 *      · Recorta `auditoria_logs_v1` a los últimos MAX_AUDIT_COMPACT_LOGS.
 *      · Poda la clave de idempotencia de ventas (entradas con TTL vencido).
 *      · Recorta las métricas internas de persistencia.
 *    Devuelve cuántos bytes se liberaron.
 *
 * QUÉ EXPORTA: factoryResetLocalData, compactLocalStorage
 *
 * QUÉ NO DEBE HACER:
 *  - No contiene lógica de negocio (ventas, inventario, cierres).
 *  - No importa React ni UI.
 *  - No toca datos fuera del origen de la app.
 */
import { txRead, txWrite } from "./transactionalStore";

const AUDIT_KEY = "auditoria_logs_v1";
const IDEMPOTENCY_KEY = "caja_venta_idempotency";
const METRICS_KEY = "__altenot_metrics__";

/** Tope de logs de auditoría que conserva la compactación. */
const MAX_AUDIT_COMPACT_LOGS = 2_000;
/** Tope de eventos de métricas internas que conserva la compactación. */
const MAX_METRICS_EVENTS = 50;

/** Espera a que una base IndexedDB quede eliminada (best-effort). */
function deleteIdbDatabase(name: string): Promise<void> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** Limpia todas las caches del Cache Storage de la app (best-effort). */
async function clearCacheStorage(): Promise<void> {
  try {
    if (typeof caches === "undefined") return;
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  } catch {
    /* caché no disponible: no es crítico */
  }
}

/**
 * Restablece la app a un estado de fábrica: borra localStorage, el espejo
 * IndexedDB, la base de datos del servicio de persistencia y las caches.
 * Después de llamarlo, la app debe recargarse (el service worker de MSW se
 * re-registra solo).
 */
export async function factoryResetLocalData(): Promise<void> {
  try {
    localStorage.clear();
  } catch {
    /* entorno sin localStorage */
  }
  await deleteIdbDatabase("altenot_mirror_v1");
  await deleteIdbDatabase("altenot_v1");
  await clearCacheStorage();
}

/**
 * Libera espacio conservando los datos de negocio. Recorta listas internas
 * que crecen sin límite (auditoría, idempotencia, métricas).
 * @returns bytes liberados aproximados.
 */
export function compactLocalStorage(): number {
  const before = (() => {
    try {
      let total = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k === null) continue;
        const v = localStorage.getItem(k);
        if (v === null) continue;
        total += (k.length + v.length) * 2;
      }
      return total;
    } catch {
      return 0;
    }
  })();

  // Auditoría: conservar los últimos MAX_AUDIT_COMPACT_LOGS.
  try {
    const logs = txRead<unknown[]>(AUDIT_KEY, []);
    if (Array.isArray(logs) && logs.length > MAX_AUDIT_COMPACT_LOGS) {
      txWrite(AUDIT_KEY, logs.slice(0, MAX_AUDIT_COMPACT_LOGS));
    }
  } catch {
    /* cuota u otro error: se omite este recorte */
  }

  // Idempotencia de ventas: podar entradas cuyo TTL ya venció.
  try {
    const store = txRead<Record<string, { ts: number }>>(IDEMPOTENCY_KEY, {});
    const now = Date.now();
    const pruned = Object.fromEntries(
      Object.entries(store).filter(([, v]) => now - Number(v.ts) < 20_000),
    );
    if (Object.keys(pruned).length !== Object.keys(store).length) {
      txWrite(IDEMPOTENCY_KEY, pruned);
    }
  } catch {
    /* best-effort */
  }

  // Métricas internas: conservar solo las últimas MAX_METRICS_EVENTS.
  try {
    const metrics = txRead<unknown[]>(METRICS_KEY, []);
    if (Array.isArray(metrics) && metrics.length > MAX_METRICS_EVENTS) {
      txWrite(METRICS_KEY, metrics.slice(-MAX_METRICS_EVENTS));
    }
  } catch {
    /* best-effort */
  }

  const after = (() => {
    try {
      let total = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k === null) continue;
        const v = localStorage.getItem(k);
        if (v === null) continue;
        total += (k.length + v.length) * 2;
      }
      return total;
    } catch {
      return 0;
    }
  })();

  return Math.max(0, before - after);
}
